import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  CheckCircle2,
  HelpCircle,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Trash2,
  X,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import apiService from '../../utils/apiService';
import {
  emptyQuizQuestionForm,
  normalizeQuizQuestion,
  normalizeQuizSettings,
  validateQuizQuestionForm,
  validateQuizSettingsForm,
} from './courseUtils';

const optionLetter = (index) => String.fromCharCode(65 + index);

const enrichQuestionsWithOptions = async (list) => {
  const base = (Array.isArray(list) ? list : []).map(normalizeQuizQuestion).filter(Boolean);
  if (!base.length) return [];

  const detailed = await Promise.all(
    base.map(async (q) => {
      try {
        const res = await apiService.getEducationQuizQuestionById(q.id);
        if (!res.success) return q;
        return normalizeQuizQuestion(res.data ?? res.result) || q;
      } catch {
        return q;
      }
    })
  );
  return detailed;
};

const QuizBuilder = ({
  lessonId,
  courseId,
  moduleId,
  backPath,
  lessonMeta,
  showSuccess,
  showError,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({
    passingScore: '70',
    maxAttempts: '3',
    timeLimitMinutes: '30',
  });
  const [settingsError, setSettingsError] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyQuizQuestionForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const courseTitle = lessonMeta?.courseTitle || settings?.courseTitle || 'Course';
  const moduleTitle = lessonMeta?.moduleTitle || settings?.moduleTitle || 'Module';
  const lessonTitle = lessonMeta?.title || settings?.title || 'Lesson';
  const isActive = lessonMeta?.isActive ?? settings?.isActive ?? true;
  const statusLabel = lessonMeta?.status || settings?.status || (isActive ? 'Active' : 'Inactive');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [settingsRes, questionsRes] = await Promise.all([
        apiService.getEducationQuizSettings(lessonId),
        apiService.getEducationQuizQuestionsList(lessonId),
      ]);

      if (!settingsRes.success) {
        setError(settingsRes.error || settingsRes.message || 'Failed to load quiz settings');
        return;
      }
      if (!questionsRes.success) {
        setError(questionsRes.error || questionsRes.message || 'Failed to load questions');
        return;
      }

      const s = normalizeQuizSettings(settingsRes.data ?? settingsRes.result);
      setSettings(s);
      setSettingsForm({
        passingScore: String(s?.passingScore ?? 70),
        maxAttempts: s?.maxAttempts == null ? '' : String(s.maxAttempts),
        timeLimitMinutes: s?.timeLimitMinutes == null ? '' : String(s.timeLimitMinutes),
      });

      const payload = questionsRes.data ?? questionsRes.result ?? [];
      const enriched = await enrichQuestionsWithOptions(payload);
      setQuestions(enriched);
    } catch (err) {
      setError(err.message || 'Failed to load quiz');
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async (e) => {
    e.preventDefault();
    const err = validateQuizSettingsForm(settingsForm);
    if (err) {
      setSettingsError(err);
      return;
    }
    setSavingSettings(true);
    setSettingsError('');
    try {
      const res = await apiService.saveEducationQuizSettings({
        lessonId: Number(lessonId),
        passingScore: Number(settingsForm.passingScore),
        maxAttempts: settingsForm.maxAttempts === '' ? null : Number(settingsForm.maxAttempts),
        timeLimitMinutes:
          settingsForm.timeLimitMinutes === '' ? null : Number(settingsForm.timeLimitMinutes),
      });
      if (!res.success) {
        showError(res.error || res.message || 'Failed to save settings');
        return;
      }
      showSuccess(res.message || 'Quiz settings saved');
      await load();
    } finally {
      setSavingSettings(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyQuizQuestionForm());
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = async (question) => {
    setFormError('');
    setActionBusy(true);
    try {
      const res = await apiService.getEducationQuizQuestionById(question.id);
      if (!res.success) {
        showError(res.error || res.message || 'Failed to load question');
        return;
      }
      const full = normalizeQuizQuestion(res.data ?? res.result);
      if (!full) {
        showError('Question not found');
        return;
      }
      setEditing(full);
      setForm({
        questionText: full.questionText || '',
        explanation: full.explanation || '',
        marks: String(full.marks || 1),
        status: full.status || 'Active',
        options: (full.options || []).map((o) => ({
          text: o.optionText || '',
          isCorrect: Boolean(o.isCorrect),
        })),
      });
      setModalOpen(true);
    } finally {
      setActionBusy(false);
    }
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    setFormError('');
  };

  const updateOption = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((o, i) => (i === index ? { ...o, ...patch } : o)),
    }));
  };

  const setCorrectOption = (index) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((o, i) => ({ ...o, isCorrect: i === index })),
    }));
  };

  const addOption = () => {
    setForm((prev) => {
      if (prev.options.length >= 6) return prev;
      return {
        ...prev,
        options: [...prev.options, { text: '', isCorrect: false }],
      };
    });
  };

  const removeOption = (index) => {
    setForm((prev) => {
      if (prev.options.length <= 2) return prev;
      const next = prev.options.filter((_, i) => i !== index);
      if (!next.some((o) => o.isCorrect) && next.length) {
        next[0] = { ...next[0], isCorrect: true };
      }
      return { ...prev, options: next };
    });
  };

  const saveQuestion = async (e) => {
    e.preventDefault();
    const err = validateQuizQuestionForm(form);
    if (err) {
      setFormError(err);
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        questionText: form.questionText.trim(),
        explanation: form.explanation.trim() || null,
        marks: Number(form.marks),
        status: form.status,
        options: form.options.map((o) => ({
          text: o.text.trim(),
          isCorrect: Boolean(o.isCorrect),
        })),
      };

      const res = editing
        ? await apiService.updateEducationQuizQuestion({
            quizQuestionId: editing.id,
            displayOrder: editing.displayOrder || 1,
            ...payload,
          })
        : await apiService.createEducationQuizQuestion({
            lessonId: Number(lessonId),
            displayOrder: 0,
            ...payload,
          });

      if (!res.success) {
        setFormError(res.error || res.message || 'Failed to save question');
        return;
      }
      showSuccess(res.message || (editing ? 'Question updated' : 'Question created'));
      setModalOpen(false);
      setEditing(null);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const moveQuestion = async (question, direction) => {
    setActionBusy(true);
    try {
      const res = await apiService.moveEducationQuizQuestion(question.id, direction);
      if (!res.success) {
        showError(res.error || res.message || 'Failed to move question');
        return;
      }
      const payload = res.data ?? res.result;
      if (Array.isArray(payload)) {
        setQuestions(await enrichQuestionsWithOptions(payload));
      } else {
        await load();
      }
    } finally {
      setActionBusy(false);
    }
  };

  const runConfirm = async () => {
    if (!confirmAction?.question) return;
    setActionBusy(true);
    try {
      const id = confirmAction.question.id;
      let res;
      if (confirmAction.type === 'activate') {
        res = await apiService.activateEducationQuizQuestion(id);
      } else if (confirmAction.type === 'inactivate') {
        res = await apiService.inactivateEducationQuizQuestion(id);
      } else {
        res = await apiService.deleteEducationQuizQuestion(id);
      }
      if (!res.success) {
        showError(res.error || res.message || 'Action failed');
        return;
      }
      showSuccess(res.message || 'Done');
      setConfirmAction(null);
      await load();
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="edu-quiz-shell">
        <AppLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="edu-quiz-shell">
        <p className="edu-form-error">{error}</p>
      </div>
    );
  }

  const confirmCopy =
    confirmAction?.type === 'delete'
      ? 'Delete question'
      : confirmAction?.type === 'activate'
        ? 'Activate question'
        : 'Inactivate question';

  const totalQuestions = settings?.totalQuestions ?? questions.length;
  const totalMarks =
    settings?.totalMarks ?? questions.reduce((sum, q) => sum + (Number(q.marks) || 0), 0);

  return (
    <div className="edu-quiz-shell">
      <nav className="edu-breadcrumb edu-quiz-breadcrumb" aria-label="Breadcrumb">
        <Link to="/education/courses">Education</Link>
        <span>/</span>
        <Link to="/education/courses">Courses</Link>
        <span>/</span>
        <Link to={`/education/courses/${courseId}`}>{courseTitle}</Link>
        <span>/</span>
        <Link to={backPath}>{moduleTitle}</Link>
        <span>/</span>
        <span>Quiz</span>
      </nav>

      <div className="edu-quiz-page-head">
        <div className="edu-quiz-page-head__main">
          <Link to={backPath} className="edu-back edu-quiz-back">
            <ArrowLeft className="w-4 h-4" /> Back to lessons
          </Link>
          <div className="edu-quiz-page-head__titles">
            <h1 className="edu-quiz-title">Quiz Builder</h1>
            <p className="edu-quiz-subtitle">
              Configure quiz settings and manage questions for this lesson.
            </p>
          </div>
        </div>
        <span className={`edu-status ${isActive ? 'is-active' : 'is-inactive'}`}>{statusLabel}</span>
      </div>

      <section className="module-card edu-card edu-quiz-info-card">
        <dl className="edu-quiz-info-grid">
          <div>
            <dt>Course</dt>
            <dd>{courseTitle}</dd>
          </div>
          <div>
            <dt>Module</dt>
            <dd>{moduleTitle}</dd>
          </div>
          <div>
            <dt>Lesson</dt>
            <dd>{lessonTitle}</dd>
          </div>
          <div>
            <dt>Type</dt>
            <dd>Quiz</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              <span className={`edu-status ${isActive ? 'is-active' : 'is-inactive'}`}>
                {statusLabel}
              </span>
            </dd>
          </div>
        </dl>
      </section>

      <section className="module-card edu-card edu-quiz-summary-card" aria-label="Quiz summary">
        <div className="edu-quiz-stat-row">
          <div className="edu-quiz-stat-block">
            <span className="edu-quiz-stat-block__label">Questions</span>
            <strong className="edu-quiz-stat-block__value">{totalQuestions}</strong>
          </div>
          <div className="edu-quiz-stat-block">
            <span className="edu-quiz-stat-block__label">Total Marks</span>
            <strong className="edu-quiz-stat-block__value">{totalMarks}</strong>
          </div>
          <div className="edu-quiz-stat-block">
            <span className="edu-quiz-stat-block__label">Passing Score</span>
            <strong className="edu-quiz-stat-block__value">{settings?.passingScore ?? 70}%</strong>
          </div>
          <div className="edu-quiz-stat-block">
            <span className="edu-quiz-stat-block__label">Attempts</span>
            <strong className="edu-quiz-stat-block__value">
              {settings?.maxAttempts == null ? 'Unlimited' : settings.maxAttempts}
            </strong>
          </div>
          <div className="edu-quiz-stat-block">
            <span className="edu-quiz-stat-block__label">Time Limit</span>
            <strong className="edu-quiz-stat-block__value">
              {settings?.timeLimitMinutes == null
                ? 'Unlimited'
                : `${settings.timeLimitMinutes} min`}
            </strong>
          </div>
        </div>
      </section>

      <section className="module-card edu-card edu-quiz-settings-card">
        <div className="edu-quiz-card-head">
          <div>
            <h2 className="edu-quiz-card-title">Quiz Settings</h2>
            <p className="edu-quiz-card-desc">Define how this quiz should be evaluated.</p>
          </div>
        </div>

        <form onSubmit={saveSettings} className="edu-quiz-settings-form">
          <div className="edu-field">
            <label className="edu-label" htmlFor="qz-pass">
              Passing Score (%)
            </label>
            <input
              id="qz-pass"
              className="edu-input"
              type="number"
              min="1"
              max="100"
              value={settingsForm.passingScore}
              onChange={(e) => {
                setSettingsForm((p) => ({ ...p, passingScore: e.target.value }));
                if (settingsError) setSettingsError('');
              }}
              disabled={savingSettings}
            />
            <span className="edu-field-hint">Percentage required to pass the quiz.</span>
          </div>
          <div className="edu-field">
            <label className="edu-label" htmlFor="qz-attempts">
              Maximum Attempts
            </label>
            <input
              id="qz-attempts"
              className="edu-input"
              type="number"
              min="1"
              placeholder="Unlimited"
              value={settingsForm.maxAttempts}
              onChange={(e) => {
                setSettingsForm((p) => ({ ...p, maxAttempts: e.target.value }));
                if (settingsError) setSettingsError('');
              }}
              disabled={savingSettings}
            />
            <span className="edu-field-hint">Leave empty for unlimited attempts.</span>
          </div>
          <div className="edu-field">
            <label className="edu-label" htmlFor="qz-time">
              Time Limit (minutes)
            </label>
            <input
              id="qz-time"
              className="edu-input"
              type="number"
              min="1"
              placeholder="Unlimited"
              value={settingsForm.timeLimitMinutes}
              onChange={(e) => {
                setSettingsForm((p) => ({ ...p, timeLimitMinutes: e.target.value }));
                if (settingsError) setSettingsError('');
              }}
              disabled={savingSettings}
            />
            <span className="edu-field-hint">Leave empty for no time limit.</span>
          </div>
          <div className="edu-quiz-settings-form__action">
            <button type="submit" className="edu-btn edu-btn--primary" disabled={savingSettings}>
              {savingSettings ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
          {settingsError ? (
            <span className="edu-form-error edu-quiz-settings-form__error">{settingsError}</span>
          ) : null}
        </form>
      </section>

      <section className="module-card edu-card edu-quiz-questions-card">
        <div className="edu-quiz-card-head edu-quiz-card-head--row">
          <div>
            <h2 className="edu-quiz-card-title">Questions</h2>
            <p className="edu-quiz-card-desc">
              Manage the questions students will answer in this quiz.
            </p>
          </div>
          {questions.length > 0 ? (
            <button type="button" className="edu-btn edu-btn--primary" onClick={openCreate}>
              <Plus className="w-4 h-4" /> Add Question
            </button>
          ) : null}
        </div>

        {questions.length === 0 ? (
          <div className="edu-quiz-empty-state">
            <div className="edu-quiz-empty-state__icon" aria-hidden="true">
              <HelpCircle className="w-6 h-6" />
            </div>
            <h3>No questions yet</h3>
            <p>Add questions to start building this quiz.</p>
            <button type="button" className="edu-btn edu-btn--primary" onClick={openCreate}>
              <Plus className="w-4 h-4" /> Add Question
            </button>
          </div>
        ) : (
          <div className="edu-quiz-question-list">
            {questions.map((q, index) => {
              const options = Array.isArray(q.options) ? q.options : [];
              return (
                <article key={q.id} className="edu-quiz-qcard">
                  <div className="edu-quiz-qcard__top">
                    <div className="edu-quiz-qcard__title-wrap">
                      <span className="edu-quiz-qcard__num">#{q.displayOrder || index + 1}</span>
                      <h3 className="edu-quiz-qcard__title">{q.questionText}</h3>
                    </div>
                    <span className={`edu-status ${q.isActive ? 'is-active' : 'is-inactive'}`}>
                      {q.status}
                    </span>
                  </div>

                  <p className="edu-quiz-qcard__meta">
                    {q.optionCount || options.length} Options · {q.marks}{' '}
                    {Number(q.marks) === 1 ? 'Mark' : 'Marks'}
                  </p>

                  {options.length > 0 ? (
                    <ul className="edu-quiz-qcard__options">
                      {options.map((opt, optIndex) => (
                        <li
                          key={opt.id || `${q.id}-${optIndex}`}
                          className={`edu-quiz-qcard__option ${
                            opt.isCorrect ? 'is-correct' : ''
                          }`}
                        >
                          <span className="edu-quiz-qcard__opt-letter">
                            {optionLetter(optIndex)}.
                          </span>
                          <span className="edu-quiz-qcard__opt-text">{opt.optionText}</span>
                          {opt.isCorrect ? (
                            <span className="edu-quiz-qcard__correct">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Correct
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <div className="edu-quiz-qcard__actions">
                    <button
                      type="button"
                      className="edu-icon-btn"
                      title="Move up"
                      aria-label="Move question up"
                      disabled={actionBusy || index === 0}
                      onClick={() => moveQuestion(q, 'up')}
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      className="edu-icon-btn"
                      title="Move down"
                      aria-label="Move question down"
                      disabled={actionBusy || index === questions.length - 1}
                      onClick={() => moveQuestion(q, 'down')}
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      className="edu-icon-btn"
                      title="Edit"
                      aria-label="Edit question"
                      disabled={actionBusy}
                      onClick={() => openEdit(q)}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {q.isActive ? (
                      <button
                        type="button"
                        className="edu-icon-btn"
                        title="Inactivate"
                        aria-label="Inactivate question"
                        onClick={() => setConfirmAction({ type: 'inactivate', question: q })}
                      >
                        <PowerOff className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="edu-icon-btn is-success"
                        title="Activate"
                        aria-label="Activate question"
                        onClick={() => setConfirmAction({ type: 'activate', question: q })}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="edu-icon-btn is-danger"
                      title="Delete"
                      aria-label="Delete question"
                      onClick={() => setConfirmAction({ type: 'delete', question: q })}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {modalOpen ? (
        <div className="edu-modal" role="presentation">
          <button type="button" className="edu-modal__backdrop" onClick={closeModal} aria-label="Close" />
          <div
            className="edu-modal__panel edu-modal__panel--form edu-modal__panel--quiz"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quiz-question-modal-title"
          >
            <div className="edu-modal__head">
              <h2 id="quiz-question-modal-title">{editing ? 'Edit Question' : 'Add Question'}</h2>
              <button
                type="button"
                className="edu-icon-btn"
                onClick={closeModal}
                disabled={saving}
                aria-label="Close dialog"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={saveQuestion}>
              <div className="edu-modal__body">
                <div className="edu-field">
                  <label className="edu-label" htmlFor="qq-text">
                    Question
                  </label>
                  <textarea
                    id="qq-text"
                    className="edu-textarea"
                    value={form.questionText}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, questionText: e.target.value }));
                      if (formError) setFormError('');
                    }}
                    disabled={saving}
                    required
                    rows={3}
                  />
                </div>
                <div className="edu-field">
                  <label className="edu-label" htmlFor="qq-expl">
                    Explanation
                  </label>
                  <textarea
                    id="qq-expl"
                    className="edu-textarea"
                    value={form.explanation}
                    onChange={(e) => setForm((p) => ({ ...p, explanation: e.target.value }))}
                    disabled={saving}
                    rows={2}
                  />
                </div>
                <div className="edu-field edu-field--narrow">
                  <label className="edu-label" htmlFor="qq-marks">
                    Marks
                  </label>
                  <input
                    id="qq-marks"
                    type="number"
                    min="1"
                    className="edu-input"
                    value={form.marks}
                    onChange={(e) => setForm((p) => ({ ...p, marks: e.target.value }))}
                    disabled={saving}
                    required
                  />
                </div>

                <div className="edu-field">
                  <label className="edu-label" id="qq-options-label">
                    Options
                  </label>
                  <p className="edu-field-hint">Select exactly one correct answer.</p>
                  <div className="edu-quiz-option-editor" role="group" aria-labelledby="qq-options-label">
                    {form.options.map((opt, index) => (
                      <div
                        key={index}
                        className={`edu-quiz-option-editor__row ${
                          opt.isCorrect ? 'is-correct' : ''
                        }`}
                      >
                        <span className="edu-quiz-option-editor__letter" aria-hidden="true">
                          {optionLetter(index)}
                        </span>
                        <input
                          className="edu-input"
                          value={opt.text}
                          placeholder={`Option ${optionLetter(index)}`}
                          onChange={(e) => updateOption(index, { text: e.target.value })}
                          disabled={saving}
                          aria-label={`Option ${optionLetter(index)} text`}
                        />
                        <label className="edu-quiz-option-editor__correct">
                          <input
                            type="radio"
                            name="correct-option"
                            checked={Boolean(opt.isCorrect)}
                            onChange={() => setCorrectOption(index)}
                            disabled={saving}
                          />
                          <span>{opt.isCorrect ? 'Correct' : 'Mark correct'}</span>
                        </label>
                        <button
                          type="button"
                          className="edu-icon-btn is-danger"
                          title="Remove option"
                          aria-label={`Remove option ${optionLetter(index)}`}
                          disabled={saving || form.options.length <= 2}
                          onClick={() => removeOption(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {form.options.length < 6 ? (
                    <button
                      type="button"
                      className="edu-btn edu-btn--ghost edu-btn--sm"
                      onClick={addOption}
                      disabled={saving}
                    >
                      <Plus className="w-4 h-4" /> Add Option
                    </button>
                  ) : null}
                </div>

                {formError ? <span className="edu-form-error">{formError}</span> : null}
              </div>
              <div className="edu-modal__foot">
                <button type="button" className="edu-btn edu-btn--ghost" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="edu-btn edu-btn--primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Question'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {confirmAction ? (
        <div className="edu-modal" role="presentation">
          <button
            type="button"
            className="edu-modal__backdrop"
            onClick={() => !actionBusy && setConfirmAction(null)}
            aria-label="Close"
          />
          <div className="edu-modal__panel" role="dialog" aria-modal="true">
            <div className="edu-modal__head">
              <h2>Confirm</h2>
              <button
                type="button"
                className="edu-icon-btn"
                disabled={actionBusy}
                onClick={() => setConfirmAction(null)}
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="edu-modal__body">
              <p>
                {confirmCopy} <strong>{confirmAction.question?.questionText}</strong>?
              </p>
            </div>
            <div className="edu-modal__foot">
              <button
                type="button"
                className="edu-btn edu-btn--ghost"
                disabled={actionBusy}
                onClick={() => setConfirmAction(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`edu-btn ${
                  confirmAction.type === 'delete' ? 'edu-btn--danger' : 'edu-btn--primary'
                }`}
                disabled={actionBusy}
                onClick={runConfirm}
              >
                {actionBusy ? 'Please wait…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default QuizBuilder;
